'use client'

import { useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import { Badge } from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  headerVariants,
  gridContainerVariants,
  gridItemVariants,
  reducedMotionVariants,
} from '../../lib/motion'

interface FAQItem {
  question: string
  answer: string
}

/**
 * Props for FAQNumberedGrid component
 *
 * @remarks
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Grid layout with configurable columns (1 for stacked, 2 for grid)
 * - Numbered indicators (1, 2, 3...) next to each question
 * - Answers support **Markdown** (bold, lists, inline code, links, paragraphs)
 * - Centered header with badge, title, and description
 * - 4-8 items recommended for balanced layout
 */
export interface FAQNumberedGridProps {
  /** Section id for anchor links */
  id?: string
  badge?: string
  title?: string
  description?: string
  items?: FAQItem[]
  /** Number of columns on desktop (1 = stacked, 2 = grid). Default: 2 */
  columns?: 1 | 2
  className?: string
}

/**
 * FAQNumberedGrid - Numbered FAQ grid layout
 *
 * **Layout:**
 * ```
 * ┌──────────────────────────────────────┐
 * │            [Badge]                   │
 * │     Common Questions & Answers       │
 * │        Description text...           │
 * │                                      │
 * │  ① Question 1?      ② Question 2?   │
 * │    Answer text...     Answer text... │
 * │                                      │
 * │  ③ Question 3?      ④ Question 4?   │
 * │    Answer text...     Answer text... │
 * └──────────────────────────────────────┘
 * ```
 *
 * @example
 * ```tsx
 * <FAQNumberedGrid
 *   badge="FAQ"
 *   title="Common Questions"
 *   description="Find answers to frequently asked questions."
 *   items={[
 *     { question: 'What is mdxui?', answer: 'A **type-safe** component library.' },
 *   ]}
 * />
 * ```
 */
const FAQNumberedGrid = ({
  id,
  badge = 'FAQ',
  title = 'Common Questions & Answers',
  description = 'Find answers to frequently asked questions about our platform.',
  items = [],
  columns = 2,
  className,
}: FAQNumberedGridProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : headerVariants
  const itemAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : gridItemVariants

  return (
    <motion.section
      id={id}
      ref={sectionRef}
      className={cn('py-16 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto">
        <motion.div className="text-center" variants={headerAnimation}>
          {badge && (
            <Badge variant="outline" className="text-xs font-medium">
              {badge}
            </Badge>
          )}
          <h1 className="mt-4 text-3xl font-semibold lg:text-4xl">{title}</h1>
          {description && (
            <div className="mt-4">
              <Markdown size="base">{description}</Markdown>
            </div>
          )}
        </motion.div>
        <motion.div
          className={cn(
            'mx-auto mt-14 grid gap-8 md:gap-12',
            columns === 2 ? 'md:grid-cols-2' : 'max-w-3xl'
          )}
          variants={gridContainerVariants(0.1, 0.2)}
        >
          {items.map((faq, index) => (
            <motion.div
              key={faq.question}
              className="flex flex-col gap-2 md:flex-row md:gap-4"
              variants={itemAnimation}
            >
              <span className="flex size-6 shrink-0 items-center justify-center rounded-[var(--radius-sm,calc(var(--radius)*0.5))] bg-muted font-mono text-xs text-foreground/50">
                {index + 1}
              </span>
              <div className="min-w-0">
                <h3 className="mb-2 font-semibold">{faq.question}</h3>
                <div className="prose prose-sm max-w-none text-muted-foreground prose-headings:text-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-pre:bg-muted prose-pre:text-foreground prose-a:text-primary prose-a:no-underline hover:prose-a:underline">
                  <ReactMarkdown>{faq.answer}</ReactMarkdown>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  )
}

export { FAQNumberedGrid }
